import { createFileRoute } from '@tanstack/react-router'

import { Footer } from '../components/landing/Footer'
import { Header } from '../components/landing/Header'
import { buildOgMeta } from '../utils/og'

const PROCESSOR_HETZNER = 'Hetzner Online GmbH'
const PROCESSOR_TRADEMEDIX = 'Trademedix Health Kft.'
const PROCESSOR_KBOSS = 'KBOSS.hu Kft. (szamlazz.hu)'

const SectionHeading = ({ number, children }: { number: string; children: React.ReactNode }) => (
    <h2 className="mt-12 text-2xl font-bold text-brand-dark">
        <span className="mr-2 text-primary">{number}.</span>
        {children}
    </h2>
)

const SubHeading = ({ children }: { children: React.ReactNode }) => (
    <h3 className="mt-8 text-lg font-bold text-brand-dark">{children}</h3>
)

const ProcessingBlock = ({
    scope,
    purpose,
    legalBasis,
    duration,
    processors,
}: {
    scope: string
    purpose: string
    legalBasis: string
    duration: string
    processors?: string[]
}) => (
    <dl className="mt-4 grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-white p-6 text-sm md:grid-cols-[220px_1fr]">
        <dt className="font-semibold text-brand-dark">Adatkezeléssel érintett adatok köre</dt>
        <dd className="text-brand-gray">{scope}</dd>
        <dt className="font-semibold text-brand-dark">Adatkezelés célja</dt>
        <dd className="text-brand-gray">{purpose}</dd>
        <dt className="font-semibold text-brand-dark">Adatkezelés jogalapja</dt>
        <dd className="text-brand-gray">{legalBasis}</dd>
        <dt className="font-semibold text-brand-dark">Adatkezelés időtartama</dt>
        <dd className="text-brand-gray">{duration}</dd>
        {processors && processors.length > 0 && (
            <>
                <dt className="font-semibold text-brand-dark">Adatfeldolgozók</dt>
                <dd className="text-brand-gray">
                    <ul className="list-disc pl-5">
                        {processors.map((p) => (
                            <li key={p}>{p}</li>
                        ))}
                    </ul>
                </dd>
            </>
        )}
    </dl>
)

function PrivacyPolicyPage() {
    return (
        <div className="min-h-screen bg-off-white font-sans text-brand-dark">
            <Header />
            <main>
                <section className="py-24">
                    <div className="mx-auto max-w-[900px] px-6">
                        <div className="mb-6 inline-block rounded-full bg-brand-yellow px-4 py-1 text-[10px] font-bold uppercase tracking-widest text-brand-dark">
                            Jogi információk
                        </div>
                        <h1 className="mb-6 text-4xl font-bold leading-tight text-brand-dark md:text-5xl">
                            Adatvédelmi tájékoztató
                        </h1>
                        <p className="text-sm text-brand-gray">
                            Hatályos: 2024. február 16. napjától
                        </p>

                        <article className="mt-12 space-y-4 text-base leading-relaxed text-brand-dark">
                            <SectionHeading number="1">A tájékoztató célja</SectionHeading>
                            <p>Jelen tájékoztató célja, hogy rögzítse a</p>
                            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm">
                                <p className="font-semibold">
                                    SOL-Y-MAR Korlátolt Felelősségű Társaság
                                </p>
                                <p>Székhely: 2083 Solymár, Bánya utca 1.</p>
                                <p>Cégjegyzékszám: 13-09-240569</p>
                                <p>Adószám: 29143005-2-13</p>
                                <p>
                                    Weboldal:{' '}
                                    <a
                                        href="https://longyapp.com/"
                                        className="text-primary hover:underline"
                                    >
                                        https://longyapp.com/
                                    </a>
                                </p>
                                <p>
                                    Email:{' '}
                                    <a
                                        href="mailto:info@longyapp.com"
                                        className="text-primary hover:underline"
                                    >
                                        info@longyapp.com
                                    </a>
                                </p>
                                <p>Képviseli: Fábián-Kovács Kinga ügyvezető</p>
                            </div>
                            <p>
                                (a továbbiakban, mint Adatkezelő) által alkalmazott adatvédelmi és
                                adatkezelési politikát, valamint, hogy az adatkezeléssel érintett
                                magánszemélyek megfelelő tájékoztatást kapjanak az Adatkezelő által
                                végzett szolgáltatások igénybevétele céljából megadott személyes
                                adataiknak a kezelésével kapcsolatban.
                            </p>
                            <p>
                                Jelen adatvédelmi tájékoztató megismerhető a{' '}
                                <a
                                    href="https://longyapp.com/"
                                    className="text-primary hover:underline"
                                >
                                    https://longyapp.com/
                                </a>{' '}
                                weboldalon online formában.
                            </p>
                            <p>
                                A jelen szabályok kialakításakor az Adatkezelő különös tekintettel
                                vette figyelembe
                            </p>
                            <ul className="list-disc pl-6">
                                <li>az Alaptörvényt;</li>
                                <li>
                                    az információs önrendelkezési jogról és az
                                    információszabadságról szóló 2011. évi CXII. törvényt (Infotv.);
                                </li>
                                <li>
                                    az egyének védelméről a személyes adatok gépi feldolgozása
                                    során, Strasbourgban, 1981. évi január 28. napján kelt egyezmény
                                    kihirdetéséről szóló 1998. évi VI. törvényt;
                                </li>
                                <li>
                                    az Európai Parlament és a Tanács 2016/679 rendeletét (GDPR
                                    rendelet).
                                </li>
                            </ul>

                            <SectionHeading number="2">
                                A tájékoztató során használt fogalmak
                            </SectionHeading>
                            <dl className="space-y-3">
                                <div>
                                    <dt className="font-semibold">Adatfeldolgozás:</dt>
                                    <dd>
                                        az adatkezelési műveletekhez kapcsolódó technikai feladatok
                                        elvégzése.
                                    </dd>
                                </div>
                                <div>
                                    <dt className="font-semibold">Adatkezelés:</dt>
                                    <dd>
                                        az alkalmazott eljárástól függetlenül az adaton végzett
                                        bármely művelet vagy a műveletek összessége, így különösen
                                        gyűjtése, felvétele, rögzítése, rendszerezése, tárolása,
                                        megváltoztatása, felhasználása, lekérdezése, továbbítása,
                                        nyilvánosságra hozatala, összehangolása vagy
                                        összekapcsolása, zárolása, törlése és megsemmisítése,
                                        valamint az adat további felhasználásának megakadályozása,
                                        fénykép-, hang- vagy képfelvétel készítése.
                                    </dd>
                                </div>
                                <div>
                                    <dt className="font-semibold">Adatkezelő:</dt>
                                    <dd>
                                        az a természetes vagy jogi személy, illetve jogi
                                        személyiséggel rendelkező társaság, aki vagy amely önállóan
                                        vagy másokkal együtt az adatok kezelésének célját
                                        meghatározza, az adatkezelésre (beleértve a felhasznált
                                        eszközt) vonatkozó döntéseket meghozza és végrehajtja, vagy
                                        az általa megbízott adatfeldolgozóval végrehajtatja.
                                    </dd>
                                </div>
                                <div>
                                    <dt className="font-semibold">Adattovábbítás:</dt>
                                    <dd>
                                        az adat meghatározott harmadik személy számára történő
                                        hozzáférhetővé tétele.
                                    </dd>
                                </div>
                                <div>
                                    <dt className="font-semibold">Adattörlés:</dt>
                                    <dd>
                                        az adatok felismerhetetlenné tétele oly módon, hogy a
                                        helyreállításuk nem lehetséges.
                                    </dd>
                                </div>
                                <div>
                                    <dt className="font-semibold">Adatvédelmi incidens:</dt>
                                    <dd>
                                        a biztonság olyan sérülése, amely a továbbított, tárolt vagy
                                        más módon kezelt személyes adatok véletlen vagy jogellenes
                                        megsemmisítését, elvesztését, megváltoztatását, jogosulatlan
                                        közlését vagy az azokhoz való jogosulatlan hozzáférést
                                        eredményezi.
                                    </dd>
                                </div>
                                <div>
                                    <dt className="font-semibold">eDM (electronic Direct Mail):</dt>
                                    <dd>
                                        A direkt marketing egyik eszköze, levélreklám, marketing
                                        üzenet. Az Érintett erre irányuló előzetes hozzájárulását
                                        követően az Adatkezelő e-mail-ben reklámot, marketing
                                        üzenetet küld az Érintett email postafiókjába. Az Adatkezelő
                                        ajánlatát az Érintett személyre szabva kapja meg.
                                    </dd>
                                </div>
                                <div>
                                    <dt className="font-semibold">Egészségügyi adat:</dt>
                                    <dd>
                                        egy természetes személy testi vagy pszichikai egészségi
                                        állapotára vonatkozó személyes adat, ideértve a természetes
                                        személy számára nyújtott egészségügyi szolgáltatásokra
                                        vonatkozó olyan adatot is, amely információt hordoz a
                                        természetes személy egészségi állapotáról.
                                    </dd>
                                </div>
                                <div>
                                    <dt className="font-semibold">Érintett:</dt>
                                    <dd>
                                        az a természetes személy, akinek az adatkezelés a személyes
                                        adatait érinti.
                                    </dd>
                                </div>
                                <div>
                                    <dt className="font-semibold">Harmadik fél:</dt>
                                    <dd>
                                        az a természetes vagy jogi személy, vagy bármely egyéb
                                        szerv, amely nem azonos az Érintettel, az Adatkezelővel, az
                                        adatfeldolgozóval vagy azokkal a személyekkel, akik az
                                        Adatkezelő vagy Adatfeldolgozó közvetlen irányítása alatt a
                                        személyes adatok kezelésére felhatalmazást kaptak.
                                    </dd>
                                </div>
                                <div>
                                    <dt className="font-semibold">Hozzájárulás:</dt>
                                    <dd>
                                        az érintett akaratának önkéntes és határozott
                                        kinyilvánítása, amely megfelelő tájékoztatáson alapul, és
                                        amellyel félreérthetetlen beleegyezését adja a rá vonatkozó
                                        személyes adatok – teljes körű vagy egyes műveletekre
                                        kiterjedő – kezeléséhez.
                                    </dd>
                                </div>
                                <div>
                                    <dt className="font-semibold">Közös adatkezelés:</dt>
                                    <dd>
                                        Ha az adatkezelés céljait és eszközeit két vagy több
                                        adatkezelő közösen határozza meg, azok közös adatkezelőknek
                                        minősülnek.
                                    </dd>
                                </div>
                                <div>
                                    <dt className="font-semibold">Személyes adat:</dt>
                                    <dd>
                                        a meghatározott természetes személlyel kapcsolatba hozható
                                        adat – különösen a neve, azonosító jele, valamint egy vagy
                                        több fizikai, fiziológiai, mentális, gazdasági, kulturális
                                        vagy szociális azonosságára jellemző ismeret –, valamint az
                                        adatból levonható, az érintettre vonatkozó következtetés,
                                        amely nem minősül közérdekű, vagy közérdekből nyilvános
                                        adatnak. Személyes adatnak minősül többek között a név, a
                                        lakcím és az e-mail cím.
                                    </dd>
                                </div>
                                <div>
                                    <dt className="font-semibold">Tiltakozás:</dt>
                                    <dd>
                                        az Érintett nyilatkozata, amellyel személyes adatainak
                                        kezelését kifogásolja, és az adatkezelés megszüntetését,
                                        illetve a kezelt adatok törlését kéri.
                                    </dd>
                                </div>
                            </dl>

                            <SectionHeading number="3">Adatkezelési alapelvek</SectionHeading>
                            <p>
                                Az Adatkezelő által végzett adatkezelés megfelel a GDPR adatkezelési
                                elveinek, amelyek a következők:
                            </p>
                            <ul className="list-disc space-y-2 pl-6">
                                <li>
                                    <strong>
                                        Jogszerűség, tisztességes eljárás és átláthatóság elve:
                                    </strong>{' '}
                                    A személyes adatok kezelését jogszerűen és tisztességesen,
                                    valamint az érintett számára átlátható módon kell végezni.
                                </li>
                                <li>
                                    <strong>Célhoz kötöttség elve:</strong> A személyes adatok
                                    gyűjtésének csak meghatározott, egyértelmű és jogszerű célból
                                    kell történnie, és azokat nem szabad a célokkal össze nem
                                    egyeztethető módon kezelni.
                                </li>
                                <li>
                                    <strong>Adattakarékosság elve:</strong> A személyes adatoknak az
                                    adatkezelés céljai szempontjából megfelelőek és relevánsak kell
                                    lenniük, és a szükségesre kell korlátozódniuk.
                                </li>
                                <li>
                                    <strong>Pontosság elve:</strong> A személyes adatoknak pontosnak
                                    és szükség esetén naprakésznek kell lenniük; minden észszerű
                                    intézkedést meg kell tenni annak érdekében, hogy az adatkezelés
                                    céljai szempontjából pontatlan személyes adatokat haladéktalanul
                                    törlésre, vagy helyesbítésre kerüljenek.
                                </li>
                                <li>
                                    <strong>Korlátozott tárolhatóság elve:</strong> A személyes
                                    adatok tárolásának olyan formában kell történnie, amely az
                                    Érintettek azonosítását csak a személyes adatok kezelése
                                    céljainak eléréséhez szükséges ideig teszi lehetővé.
                                </li>
                                <li>
                                    <strong>Integritás és bizalmas jelleg elve:</strong> A személyes
                                    adatok kezelését oly módon kell végezni, hogy megfelelő
                                    technikai vagy szervezési intézkedések alkalmazásával biztosítva
                                    legyen a személyes adatok megfelelő biztonsága, az adatok
                                    jogosulatlan vagy jogellenes kezelésével, véletlen
                                    elvesztésével, megsemmisítésével vagy károsodásával szembeni
                                    védelmet is ideértve.
                                </li>
                                <li>
                                    <strong>Elszámoltathatóság elve:</strong> Az Adatkezelő felelős
                                    az elveknek való megfelelésért, továbbá képesnek kell lenniük
                                    ezen megfelelés igazolására.
                                </li>
                            </ul>
                            <p>
                                Az adatkezelés elvei mellet, közös követelményként azonosítható a
                                megfelelő tájékoztatás követelménye, mivel az Adatkezelőnek bármely
                                adatkezelési jogalap esetén tájékoztatnia kell az Érintetteket az
                                adatkezelésről.
                            </p>

                            <SectionHeading number="4">
                                A kezelt adatok köre, az adatkezelés jogalapja, célja, és az
                                adatkezelés időtartama
                            </SectionHeading>

                            <SubHeading>4.1. Regisztráció – fiók létrehozása</SubHeading>
                            <p>
                                Az Adatkezelő által nyújtott szolgáltatások igénybevételéhez minden
                                esetekben szükség van a regisztrációs formanyomtatvány kitöltésére,
                                amelyet a{' '}
                                <a
                                    href="https://longyapp.com/"
                                    className="text-primary hover:underline"
                                >
                                    https://longyapp.com/
                                </a>{' '}
                                weboldalon, valamint a Long-Y mobil applikációban talál meg az
                                Érintett. Az Adatkezelő regisztrációhoz kötött oldalainak
                                használatához az Érintetteknek a regisztráció során a valóságnak,
                                illetve a tényleges állapotnak megfelelő adatokat kötelesek
                                szolgáltatni. A szolgáltatások igénybevételéhez elengedhetetlen a
                                név, e-mail cím, telefonszám megadása.
                            </p>
                            <ProcessingBlock
                                scope="Az Érintett neve, email címe, telefonszáma, születési hely és idő, anyja leánykori neve, Azonosító, lakóhely, adóazonosító jel"
                                purpose="Az Adatkezelő által nyújtott szolgáltatások igénybevétele"
                                legalBasis="Az Érintett hozzájárulását adta személyes adatainak egy vagy több konkrét célból történő kezeléséhez – GDPR 6. cikk (1) a) pont"
                                duration="Az Adatkezelő az Érintett törlésre irányuló kérelméig (tehát a regisztráció törléséig) kezeli a személyes adatokat."
                                processors={[
                                    PROCESSOR_HETZNER,
                                    PROCESSOR_TRADEMEDIX,
                                    'Google LLC.',
                                ]}
                            />

                            <SubHeading>4.2. Belépés a felhasználói felületre</SubHeading>
                            <p>
                                Az Érintettnek az Adatkezelő által nyújtott szolgáltatások
                                igénybevételéhez a regisztrációt követően be kell jelentkeznie a{' '}
                                <a
                                    href="https://longyapp.com/"
                                    className="text-primary hover:underline"
                                >
                                    https://longyapp.com/
                                </a>{' '}
                                weboldalon, vagy a Long-Y mobil applikációban a Belépés menüpontban.
                            </p>
                            <ProcessingBlock
                                scope="Az Érintett e-mail címe"
                                purpose="Az Adatkezelő által nyújtott szolgáltatások igénybevétele"
                                legalBasis="Az Érintett hozzájárulását adta személyes adatainak egy vagy több konkrét célból történő kezeléséhez – GDPR 6. cikk (1) a) pont"
                                duration="Az Adatkezelő az Érintett törlésre irányuló kérelméig (tehát a regisztráció törléséig) kezeli a személyes adatokat."
                                processors={[
                                    PROCESSOR_HETZNER,
                                    PROCESSOR_TRADEMEDIX,
                                    'Google LLC.',
                                ]}
                            />

                            <SubHeading>4.3. Sütik</SubHeading>
                            <p>
                                A cookie (süti) egy olyan kisméretű szövegfájl, amely az Érintett
                                számítógépe vagy a mobil eszköze merevlemezén tárolódik a cookie-ban
                                beállított lejárati ideig, és a későbbi látogatásokkor újra
                                aktiválódik. Célja, hogy rögzítse a látogatással kapcsolatos
                                információkat, illetve a személyes beállításokat, ezek azonban a
                                látogató személyével kapcsolatba nem hozható adatok. Segít a
                                felhasználóbarát weboldal kialakításában, valamint az Érintett
                                online élményének fokozása érdekében. Ha az Érintett nem egyezik
                                bele, hogy az Adatfeldolgozó cookie-kat használjon, amikor az
                                Érintett a weboldalon böngészik, előfordulhat, hogy weboldal nem fog
                                teljeskörűen működni.
                            </p>
                            <ProcessingBlock
                                scope="Az Adatkezelő minden elemzési információt név, vagy más személyes adat nélkül tárol"
                                purpose="Az érintett személyes beállításainak a tárolása"
                                legalBasis="Az Érintett hozzájárulását adta személyes adatainak egy vagy több konkrét célból történő kezeléséhez – GDPR 6. cikk (1) a) pont"
                                duration="Az érintett a böngészője beállításain keresztül bármikor törölheti a számítógépén vagy mobiltelefonján"
                            />

                            <SubHeading>4.4. Szakértők bemutatása</SubHeading>
                            <p>
                                Az Adatkezelő weboldalán a{' '}
                                <a
                                    href="https://longyapp.com/"
                                    className="text-primary hover:underline"
                                >
                                    https://longyapp.com/
                                </a>{' '}
                                vagy a Long-Y mobil applikációban a szolgáltatásokat igénybe vevő
                                felhasználók megismerhetik az Adatkezelővel szerződésben álló
                                orvosokat. Az adatkezelés során az Érintettek az orvosok.
                            </p>
                            <ProcessingBlock
                                scope="Az Érintett neve és képmása, szakterülete, illetve az általa a bemutatkozás során önkéntesen megadott egyéb adatok"
                                purpose="Az adatkezelés célja, hogy az Adatkezelő szolgáltatását igénybe vevők megismerhessék az szolgáltatások során közreműködő szakértőket."
                                legalBasis="Az Érintett hozzájárulását adta személyes adatainak egy vagy több konkrét célból történő kezeléséhez – GDPR 6. cikk (1) a) pont"
                                duration="Az Adatkezelő és a szakértő közötti együttműködés megszűnését követően megszűnik az adatkezelés"
                                processors={[PROCESSOR_HETZNER, PROCESSOR_TRADEMEDIX]}
                            />

                            <SubHeading>4.5. Adatkezelés a számlázás során</SubHeading>
                            <p>
                                Az adatkezelés a jogszabályoknak megfelelő számla kiállítása és a
                                számviteli bizonylatmegőrzési kötelezettség teljesítése érdekében
                                történik.
                            </p>
                            <ProcessingBlock
                                scope="Az Érintett neve, lakcíme és/vagy tartózkodási helye, telefonszáma, email címe"
                                purpose="Számla kiállítása és a számviteli bizonylatmegőrzési kötelezettség teljesítése"
                                legalBasis="Az adatkezelés az adatkezelőre vonatkozó jogi kötelezettség teljesítéséhez szükséges (GDPR Rendelet)"
                                duration="Számviteli törvény szerint, jelenleg 8 év"
                                processors={[
                                    PROCESSOR_HETZNER,
                                    PROCESSOR_TRADEMEDIX,
                                    PROCESSOR_KBOSS,
                                    'Stripe, LLC',
                                    'Reliable Account Kft.',
                                ]}
                            />

                            <SubHeading>
                                4.6. Adatkezelés szerződéskötés és teljesítés során
                            </SubHeading>
                            <p>
                                Amennyiben az Adatkezelő és egy partnere között szerződés jön létre,
                                úgy a felek megjelölik (megjelölhetik) a szerződésben a
                                kapcsolattartó magánszemélyeket, és azok elérhetőségét.
                            </p>
                            <ProcessingBlock
                                scope="Az Érintett neve, telefonszáma, beosztása és email címe"
                                purpose="A vállalkozások közötti kapcsolattartás, a szerződésekben foglaltak teljesülése"
                                legalBasis="Az adatkezelés olyan szerződés teljesítéséhez szükséges, amelyben az érintett az egyik fél, vagy az a szerződés megkötését megelőzően az érintett kérésére történő lépések megtételéhez szükséges (GDPR Rendelet)"
                                duration="A szerződéses jogviszony megszűnéséig, illetve a vonatkozó jogszabályokban előírtaknak megfelelő ideig"
                                processors={[
                                    PROCESSOR_HETZNER,
                                    PROCESSOR_TRADEMEDIX,
                                    PROCESSOR_KBOSS,
                                ]}
                            />

                            <SectionHeading number="5">
                                Az Adatkezelő által végzett szolgáltatásokhoz kapcsolódó
                                adatkezelések
                            </SectionHeading>

                            <SubHeading>5.1. Szakértői chat</SubHeading>
                            <p>
                                Az Adatkezelő „szakértői chat" szolgáltatása rövid, célzott
                                konzultációt biztosít az Érintettek számára egy adott kérdés vagy
                                probléma kapcsán. A beérkező megkeresésekre az Érintettek legfeljebb
                                48 órán belül személyre szabott választ kapnak. Az adatkezelés
                                keretében az Adatkezelővel szerződéses jogviszonyban álló szakértők
                                adatfeldolgozónak minősülnek, mivel a szolgáltatás nyújtása
                                érdekében megismerhetik az Érintettek személyes adatait, és azokra
                                alapozva adnak szakmai tanácsot.
                            </p>
                            <ProcessingBlock
                                scope="Az Érintett neve, e-mail címe, számlázási adatok, és az általa megadott, előre meg nem határozható egészségügyi és egyéb személyes adatok"
                                purpose="Az Adatkezelő „szakértői chat” szolgáltatásának igénybevétele"
                                legalBasis="Az Érintett hozzájárulását adta személyes adatainak egy vagy több konkrét célból történő kezeléséhez – GDPR 6. cikk (1) a) pont"
                                duration="Az üzenetek minőségbiztosítási okból 2 évig kerülnek tárolásra"
                                processors={[
                                    PROCESSOR_HETZNER,
                                    PROCESSOR_TRADEMEDIX,
                                    PROCESSOR_KBOSS,
                                    'Twilio Inc.',
                                ]}
                            />

                            <SubHeading>5.2. Videókonzultáció</SubHeading>
                            <p>
                                Az Adatkezelő „videókonzultáció" elnevezésű szolgáltatása keretében
                                az Érintettek számára lehetőség biztosított arra, hogy előre
                                egyeztetett időpontban, online formában konzultáljanak az
                                Adatkezelővel szerződéses jogviszonyban álló, életmód- és longevity
                                témákban jártas szakértőkkel. A szolgáltatás célja, hogy az
                                Érintettek személyre szabott, általános életmódbeli és
                                egészségmegőrzési tanácsokat kapjanak.
                            </p>
                            <p>
                                A szolgáltatás teljesítése során az Adatkezelővel szerződéses
                                jogviszonyban álló szakértők az Érintettek személyes adatainak
                                megismerésére jogosultak, és azok kezelése tekintetében
                                adatfeldolgozónak minősülnek, figyelemmel arra, hogy az adatkezelést
                                az Adatkezelő nevében és utasításai alapján végzik, kizárólag a
                                szolgáltatás nyújtása céljából.
                            </p>
                            <ProcessingBlock
                                scope="Az Érintett neve, e-mail címe, számlázási adatok, és az általa megadott, előre meg nem határozható egészségügyi és egyéb személyes adatok"
                                purpose="Az Adatkezelő „videókonzultáció” szolgáltatásának igénybevétele"
                                legalBasis="Az Érintett hozzájárulását adta személyes adatainak egy vagy több konkrét célból történő kezeléséhez – GDPR 6. cikk (1) a) pont"
                                duration="Az üzenetek minőségbiztosítási okból 2 évig kerülnek tárolásra"
                                processors={[
                                    PROCESSOR_HETZNER,
                                    PROCESSOR_TRADEMEDIX,
                                    PROCESSOR_KBOSS,
                                    'LiveKit, Inc.',
                                ]}
                            />

                            <SubHeading>5.3. Időpontfoglalás</SubHeading>
                            <p>
                                Az Érintettek számára az Adatkezelő felületén lehetőség biztosított
                                arra, hogy előre egyeztetett időpontban személyes konzultációra,
                                valamint egészségügyi szolgáltatások – így különösen
                                járóbeteg-ellátás, laborvizsgálatok, dietetikai vagy szakorvosi
                                konzultáció – igénybevételére időpontot foglaljanak.
                            </p>
                            <ProcessingBlock
                                scope="Az Érintett neve, e-mail címe, számlázási adatok, és az általa megadott, előre meg nem határozható egészségügyi és egyéb személyes adatok"
                                purpose="Az Adatkezelő „időpontfoglalás” szolgáltatásának igénybevétele"
                                legalBasis="Az Érintett hozzájárulását adta személyes adatainak egy vagy több konkrét célból történő kezeléséhez – GDPR 6. cikk (1) a) pont"
                                duration="2 évig kerülnek tárolásra"
                                processors={[
                                    PROCESSOR_HETZNER,
                                    PROCESSOR_TRADEMEDIX,
                                    PROCESSOR_KBOSS,
                                ]}
                            />

                            <SectionHeading number="6">Adatfeldolgozók</SectionHeading>
                            <p>
                                Az Adatkezelő a személyes adatok kezelése során az alábbi
                                adatfeldolgozókat veszi igénybe:
                            </p>
                            <div className="overflow-x-auto">
                                <table className="mt-4 w-full border-collapse text-sm">
                                    <thead>
                                        <tr className="bg-primary/5 text-left">
                                            <th className="border-b border-slate-200 p-3 font-semibold">
                                                Adatfeldolgozó neve
                                            </th>
                                            <th className="border-b border-slate-200 p-3 font-semibold">
                                                Elérhetősége
                                            </th>
                                            <th className="border-b border-slate-200 p-3 font-semibold">
                                                Feladatköre
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="border-b border-slate-100 p-3">
                                                Trademedix Health Kft.
                                            </td>
                                            <td className="border-b border-slate-100 p-3">
                                                2083 Solymár, Bánya utca 1.
                                            </td>
                                            <td className="border-b border-slate-100 p-3">
                                                Fejlesztés, IT üzemeltetés
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="border-b border-slate-100 p-3">
                                                Google LLC
                                            </td>
                                            <td className="border-b border-slate-100 p-3">
                                                Googleplex 1600 Amphitheatre Pkwy, Mountain View,
                                                California, USA
                                            </td>
                                            <td className="border-b border-slate-100 p-3">
                                                Telefonszám hitelesítés
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="border-b border-slate-100 p-3">
                                                Twilio Inc.
                                            </td>
                                            <td className="border-b border-slate-100 p-3">
                                                645 Harrison Street 3rd Floor San Francisco, CA
                                                94107 United States
                                            </td>
                                            <td className="border-b border-slate-100 p-3">
                                                Szakértői chat
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="border-b border-slate-100 p-3">
                                                KBOSS.hu Kereskedelmi és Szolgáltató Kft.
                                                (számlázz.hu)
                                            </td>
                                            <td className="border-b border-slate-100 p-3">
                                                1031 Budapest, Záhony utca 7.
                                            </td>
                                            <td className="border-b border-slate-100 p-3">
                                                Számlázás
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="border-b border-slate-100 p-3">
                                                Hetzner Online GmbH
                                            </td>
                                            <td className="border-b border-slate-100 p-3">
                                                Industriestr. 25, 91710 Gunzenhausen, Germany
                                            </td>
                                            <td className="border-b border-slate-100 p-3">
                                                Szerverkörnyezet
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="border-b border-slate-100 p-3">
                                                LiveKit, Inc.
                                            </td>
                                            <td className="border-b border-slate-100 p-3">
                                                285 Payne Avenue, Suite 9154, San Jose, CA 95157,
                                                USA
                                            </td>
                                            <td className="border-b border-slate-100 p-3">
                                                Videókonzultáció
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="border-b border-slate-100 p-3">
                                                Stripe, LLC
                                            </td>
                                            <td className="border-b border-slate-100 p-3">
                                                1 Wilton Park, Wilton Terrace, Dublin 2, Írország
                                            </td>
                                            <td className="border-b border-slate-100 p-3">
                                                Bankkártyás fizetés
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-3">Reliable Account Kft.</td>
                                            <td className="p-3">Törökbálint, Tópark u. 3, 2045</td>
                                            <td className="p-3">Könyvelés</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <p>
                                Az Adatfeldolgozók adatkezeléssel összefüggő tevékenysége kizárólag
                                a szükséges technikai és informatikai háttér biztosítására terjed
                                ki. Az Adatfeldolgozók az adatkezeléssel kapcsolatban önálló
                                döntéshozatalra nem jogosultak; a személyes adatokat kizárólag az
                                Adatkezelő utasításai alapján és annak nevében kezelhetik. Az
                                általuk megismert személyes adatokat saját céljaikra nem
                                használhatják fel, továbbá kötelesek azokat az Adatkezelő
                                rendelkezéseinek megfelelően kezelni, tárolni és megőrizni.
                            </p>
                            <p>
                                Az Adatkezelő szolgáltatásainak megrendelése esetén az Érintett a
                                fizetés lebonyolításához a Stripe elektronikus fizetési
                                szolgáltatást veheti igénybe. A Stripe egy nemzetközi online
                                fizetési szolgáltató, amely lehetővé teszi a bankkártyás és egyéb
                                elektronikus fizetések biztonságos és kényelmes teljesítését az
                                Adatkezelő felületein.
                            </p>
                            <p>
                                A szolgáltatások igénybevételével és jelen tájékoztató elfogadásával
                                az Érintett kifejezetten tudomásul veszi, hogy az online fizetés
                                során megadott, titkosított (kódolt) adatait a fizetési
                                szolgáltatást nyújtó Stripe, Inc. (székhely: 354 Oyster Point
                                Boulevard, South San Francisco, CA 94080, Amerikai Egyesült Államok)
                                megismerheti.
                            </p>
                            <p>
                                A Stripe által a fizetési tranzakciókról rögzített adatok az
                                Adatkezelő részére továbbításra kerülnek annak érdekében, hogy az
                                Adatkezelő ellenőrizni tudja a fizetés teljesítését.
                            </p>
                            <p>
                                Az Érintett a Stripe szolgáltatást az Adatkezelő felületébe
                                integrált alkalmazásprogramozási interfészen (API) keresztül veszi
                                igénybe. Bár az API az Adatkezelő felületén jelenik meg, a megadott
                                fizetési adatok közvetlenül a Stripe rendszerébe kerülnek
                                továbbításra, így azokat az Adatkezelő nem ismeri meg és nem
                                tárolja.
                            </p>
                            <p>
                                A Stripe, mint adatfeldolgozó a fizetési folyamat során az alábbi
                                adatokat ismerheti meg: az Érintett neve, számlázási adatai (név,
                                irányítószám, város, cím), valamint e-mail címe.
                            </p>

                            <SectionHeading number="7">Adattovábbítás</SectionHeading>
                            <p>
                                Az Adatkezelő a birtokába jutott személyes adatokat harmadik fél
                                részére az Érintett előzetes hozzájárulása nélkül nem továbbítja,
                                kivéve azon eseteket, amikor az adattovábbításra jogszabály kötelezi
                                – ideértve különösen a hatóságok vagy bíróságok megkeresése alapján
                                történő adatszolgáltatást.
                            </p>

                            <SectionHeading number="8">Adatbiztonság</SectionHeading>
                            <p>
                                Az Adatkezelő a fentiekben megnevezett személyes adatokat a
                                székhelyén, a saját informatikai rendszerében, továbbá a
                                tárhelyszolgáltatásért felelős Adatfeldolgozók szerverein tárolja.
                            </p>
                            <p>
                                Az Adatkezelők kötelezi magát arra vonatkozóan, hogy a GDPR-ban és
                                az Infotv.-ben foglaltaknak megfelelően, az Érintettek jogait szem
                                előtt tartva gondoskodnak az adatok biztonságáról.
                            </p>
                            <p>
                                Az esetleges adatvédelmi incidensekről nyilvántartást vezetnek,
                                amennyiben szükséges, a felmerülő incidensekről tájékoztatják az
                                Érintettet, valamint szükség esetén a Nemzeti Adatvédelmi és
                                Információszabadság Hatóságot (NAIH).
                            </p>
                            <p>
                                A személyes adatokhoz az Adatkezelő érdekkörében eljáró azon
                                személyek – így különösen megbízottak, munkavállalók – férnek hozzá,
                                akiknek ez tevékenységük ellátásához szükséges, és akik az adatok
                                kezelésével kapcsolatos kötelezettségekkel tisztában vannak, azokat
                                ismerik.
                            </p>
                            <p>
                                Az Adatkezelő megtesz minden szükséges intézkedést annak érdekében,
                                hogy biztosítsa az adatok biztonságos, sérülésmentes kezelését, és
                                az ehhez szükséges adatkezelési rendszerek kiépítését, működtetését.
                                Az Adatkezelő gondoskodik arról, hogy a kezelt adatokhoz
                                illetéktelen személy ne férhessen hozzá, ne hozhassa nyilvánosságra,
                                ne továbbíthassa, valamint azokat ne módosíthassa, törölhesse.
                            </p>
                            <p>
                                Az Adatkezelő kötelezettséget vállal arra, hogy a legkorszerűbb és
                                legmegfelelőbb berendezésekkel és biztonsági szabályokkal
                                gondoskodik az adatok biztonságáról, különös tekintettel arra, hogy
                                az adatokhoz jogosulatlan személy hozzá ne férjen, az adatok
                                jogszerűtlen ne kerüljenek nyilvánosságra, törlésre vagy
                                megsemmisítésre. Megtesz minden tőle telhetőt annak érdekében, hogy
                                az adatok véletlenül se sérüljenek, illetve semmisüljenek meg. A
                                fenti kötelezettségvállalást az Adatkezelő adatkezelési
                                tevékenységben részt vevő munkavállalói részére is előírja.
                            </p>
                            <p>
                                Az Adatkezelő semmilyen körülmények között nem gyűjt különleges
                                adatokat, azaz olyan adatokat, amelyek faji eredetre, a nemzeti és
                                etnikai kisebbséghez tartozásra, a politikai véleményre vagy
                                pártállásra, a vallásos vagy más világnézeti meggyőződésre, az
                                érdek-képviseleti szervezeti tagságra, az egészségi állapotra, a
                                kóros szenvedélyre, a szexuális életre, valamint büntetett előéletre
                                vonatkoznak.
                            </p>

                            <SectionHeading number="9">Adatvédelmi tisztviselő</SectionHeading>
                            <p>
                                Az Adatvédelmi tisztviselő ellátja az Adatkezelőt az adatvédelemmel
                                kapcsolatos szakmai tanácsadással, ellenőrzi az adatkezeléssel
                                kapcsolatos tevékenységet, valamint az illetékes hatósággal és az
                                Érintettekkel történő kapcsolattartással segíti az Adatkezelő
                                működését.
                            </p>

                            <SectionHeading number="10">
                                Az Érintett jogai az adatkezelés során
                            </SectionHeading>
                            <p>
                                Az adatkezelés időtartamán belül az Érintettet az alábbi jogok
                                illetik meg:
                            </p>
                            <SubHeading>Tájékoztatáshoz való jog</SubHeading>
                            <p>
                                Megfelelő módon, egyszerű és befogadható nyelvezetű, könnyen
                                fellelhető (online vagy offline) információt köteles adni az
                                Adatkezelő az adatkezelés lényeges szempontjairól. A személyes
                                adatok megszerzésének időpontjában, vagy amennyiben az Érintett
                                utólag kér tájékoztatást, ezen tájékoztatás megadásakor az Érintett
                                rendelkezésére kell bocsátani az Adatkezelési tájékoztatót, és az
                                abban foglaltak megismerését, megértését és elfogadását igazoló
                                nyilatkozatot kell aláíratni vele. Az Érintett bármikor jogosult
                                tájékoztatást kérni az Adatkezelő által kezelt, rá vonatkozó
                                személyes adatokról. A tájékoztatás az adott adatkezelésről készült
                                tájékoztatóban megjelölt elektronikus e-mail címen
                                (info@longyapp.hu) postai úton (2083 Solymár, Bánya utca 1.) is
                                kérhető. Az Adatkezelő a kérelem alapján 30 napon belül köteles
                                megadni a kért tájékoztatást.
                            </p>
                            <SubHeading>Törléshez való jog</SubHeading>
                            <p>
                                Az Érintett jogosult arra, hogy kérésére az Adatkezelő indokolatlan
                                késedelem nélkül törölje a rá vonatkozó személyes adatokat,
                                amennyiben a törlés feltételei a General Data Protection Regulation
                                alapján fennállnak. Az Adatkezelő köteles az Érintettre vonatkozó
                                személyes adatokat indokolatlan késedelem nélkül törölni.
                            </p>
                            <p>
                                Amennyiben az Adatkezelő a személyes adatokat harmadik felek részére
                                továbbította vagy azokhoz hozzáférést biztosított, úgy köteles az
                                ésszerűen elvárható intézkedéseket megtenni annak érdekében, hogy az
                                érintett személyes adatok törléséről tájékoztassa ezen címzetteket,
                                ideértve a hivatkozások, másolatok és további adatfeldolgozások
                                törlését is.
                            </p>
                            <p>
                                A törléshez való jog gyakorlásának célja, hogy – amennyiben annak
                                jogszabályi vagy egyéb jogszerű akadálya nincs – az Érintettre
                                vonatkozó személyes adatok a lehető legszélesebb körben
                                eltávolításra kerüljenek a nyilvántartásokból és adatbázisokból.
                            </p>
                            <p>
                                A felhasználó fiókját a Long-Y mobil applikáció fiókon belül a
                                Beállítások/Jelszó és biztonság/Fiók törlése elérési útvonalon tudja
                                törölni. A felhasználói fiók törlését kérheti az info@longyapp.hu
                                e-mail címen keresztül is.
                            </p>
                            <p>A törést nem kell teljesíteni, amennyiben az adatkezelés</p>
                            <ul className="list-disc pl-6">
                                <li>
                                    véleménynyilvánítás szabadságához, vagy a tájékoztatáshoz való
                                    jog gyakorlása céljából szükséges;
                                </li>
                                <li>
                                    jogi igények előterjesztéséhez, érvényesítéséhez és védelméhez
                                    szükséges;
                                </li>
                                <li>jogi kötelezettség teljesítése miatt szükséges;</li>
                                <li>
                                    közérdekű archiválás, tudományos, vagy történelmi kutatás,
                                    statisztikai célból szükséges és az adattörlés lehetetlenné
                                    tenné, vagy komolyan veszélyeztetné az adatkezelés céljának
                                    teljesítését.
                                </li>
                            </ul>
                            <p>
                                Az Adatkezelő törli továbbá az Érintettre vonatkozó dokumentációiban
                                szereplő személyes adatokat, ha a személyes adat kezeléséhez fűződő
                                cél megszűnt. A papír alapú dokumentációk esetében azok
                                jegyzőkönyvvel rögzített megsemmisítéséről kell gondoskodni abból az
                                okból kifolyólag, hogy annak tényét a későbbiekben az illetékes
                                hatóság felé bizonyíthassák.
                            </p>
                            <SubHeading>Az adatok helyesbítése</SubHeading>
                            <p>
                                Az Érintett jelezheti, hogy a kezelt adatok pontatlanok, és kérheti,
                                hogy azok helyett mi kerüljön feltüntetésre. Az adatok pontosságáért
                                az Adatkezelőt terheli a felelősség, így időről időre szükséges
                                ellenőrizni azok pontosságát.
                            </p>
                            <SubHeading>Az adatkezelés korlátozásához való jog</SubHeading>
                            <p>
                                Az Érintett személy kérheti személyes adatai kezelésének
                                korlátozását az Adatkezelőtől például egy tisztázatlan, jogvitás
                                helyzetben. Ha az adatkezelés korlátozás alá esik, az ilyen
                                személyes adatokat a tárolás kivételével csak az Érintett
                                hozzájárulásával, vagy jogi igények előterjesztéséhez,
                                érvényesítéséhez vagy védelméhez, vagy más természetes vagy jogi
                                személy jogainak védelme érdekében, vagy az Európai Unió, illetve
                                valamely tagállam fontos közérdekéből lehet kezelni.
                            </p>
                            <SubHeading>Adathordozhatósághoz való jog</SubHeading>
                            <p>
                                Az Érintett kérheti, hogy a rá vonatkozóan kezelt adatokat tagolt,
                                széles körben használt, géppel olvasható formátumban (pl. .doc, .pdf
                                stb.) megkapja, továbbá jogosult arra, hogy ezeket az adatokat egy
                                másik Adatkezelőnek továbbítsa anélkül, hogy ezt akadályozná az
                                eredeti Adatkezelő. Megkönnyíti az adatkezeléssel érintett
                                helyzetét, hogy személyes adatait egyik Adatkezelőtől a másikhoz
                                vigye át.
                            </p>
                            <SubHeading>Tiltakozáshoz való jog</SubHeading>
                            <p>
                                Az Érintett jogosult arra, hogy amennyiben nem adta hozzájárulását
                                az adatok kezeléséhez, bármikor tiltakozzon személyes adatainak
                                meghatározott okból történő kezelése ellen.
                            </p>
                            <p>
                                Amennyiben az Érintett a jogaival élni kíván, az az azonosításával
                                jár együtt, valamint az Érintettel szükségszeren kommunikálniuk kell
                                az Adatkezelőnek, ezért az azonosítás érdekében személyes adatok
                                megadására lesz szükség, valamint az email fiókban elérhetőek lesz
                                az Érintett adatkezeléssel kapcsolatos panasza a jelen
                                tájékoztatóban, a panaszokkal kapcsolatban megjelölt időtartamon
                                belül. Az adatkezeléssel kapcsolatos panaszokat haladéktalanul, de
                                legkésőbb 30 napon belül válaszolja meg az Adatkezelő.
                            </p>

                            <SectionHeading number="11">Jogorvoslati lehetőségek</SectionHeading>
                            <p>
                                Az Érintett jogosult panaszával a NAIH-hoz (1055 Budapest, Falk
                                Miksa u. 9-11.;{' '}
                                <a
                                    href="https://www.naih.hu"
                                    className="text-primary hover:underline"
                                >
                                    www.naih.hu
                                </a>
                                , Telefon: +36 (1) 391-1400, Telefax: +36 (1) 391-1410, E-mail:{' '}
                                <a
                                    href="mailto:ugyfelszolgalat@naih.hu"
                                    className="text-primary hover:underline"
                                >
                                    ugyfelszolgalat@naih.hu
                                </a>
                                ) fordulni vagy a Polgári perrendtartásról szóló 2016. évi CXXX.
                                törvény szerint hatáskörrel és illetékességgel rendelkező Bíróság
                                előtt érvényesíteni személyes adatok kezelésével kapcsolatos jogait.
                            </p>

                            <SectionHeading number="12">Záró rendelkezések</SectionHeading>
                            <p>
                                Amennyiben az Adatkezelő a személyes adatokkal kapcsolatban a jelen
                                tájékoztatóban foglalt céltól eltérő célból további adatkezelést
                                kíván végezni, a további adatkezelést megelőzően tájékoztatja az
                                Érintettet az adatkezelés új céljáról. Az új célból történő
                                adatkezelés csak ezt követően kezdődhető meg – amennyiben az
                                adatkezelés jogalapja hozzájárulás – ha az adatkezeléshez a
                                tájékoztatáson felül az Érintett is hozzájárul.
                            </p>
                            <p>
                                Az Adatkezelő fenntartja magának a jogot a jelen tájékoztató
                                megváltoztatására, illetőleg a közösségi vagy a hazai jogszabályok
                                változásai esetén a tájékoztató megfelelő módosítására.
                            </p>
                            <p>
                                Jelen Adatvédelmi Tájékoztató 2024. február 16. napjától érvényes.
                            </p>

                            <div className="mt-12 border-t border-slate-200 pt-6 text-sm text-brand-gray">
                                <p>SOL-Y-MAR Spirit Kft.</p>
                                <p>Képviseli: Fábián-Kovács Kinga ügyvezető</p>
                                <p>Adatkezelő</p>
                            </div>
                        </article>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    )
}

export const Route = createFileRoute('/privacy-policy')({
    component: PrivacyPolicyPage,
    loader: async ({ context }) => {
        const [, websiteSettings] = await Promise.allSettled([
            context.apiUtils.webContent.getAllPublic.ensureData(),
            context.apiUtils.websiteSettings.getPublic.ensureData(),
        ])
        return {
            websiteSettings: websiteSettings.status === 'fulfilled' ? websiteSettings.value : null,
        }
    },
    head: ({ loaderData }) => ({
        meta: buildOgMeta({
            title: 'Long-Y - Adatvédelmi tájékoztató',
            url: `${import.meta.env.VITE_WEB_URL || ''}/privacy-policy`,
            websiteSettings: loaderData?.websiteSettings,
        }),
    }),
})
